import type { AlertProps } from '@mui/material/Alert';
import firebase from 'firebase/compat/app';
import { atom, type WritableAtom } from 'jotai';
import { atomFamily, atomWithDefault, RESET } from 'jotai/utils';
import type Stripe from 'stripe';
import '../config/initializeApp';
import { endpoint } from '../env';
import { requestWithAuth } from '../store/requestWithAuth';
import {
  convertToAdminDetail,
  convertToMember,
  convertToPasswordHistory,
  convertToTeam,
  convertToUser
} from '../utils/converters';

/**
 * ユーザー情報を取得する Jotai 版
 */
export const getUserByIdAtom = atomFamily((id: string) =>
  atom(async () => {
    const snapshot = await firebase
      .firestore()
      .collection('users')
      .doc(id)
      .withConverter(convertToUser)
      .get();
    return snapshot.data();
  })
);

/**
 * ポップアップ通知の Jotai 版
 */
export const popupAlertAtom = atom<AlertProps | undefined>(undefined);

/**
 * Firebase Authentication の現在の認証情報 (Jotai 版)
 * undefined であれば状態は未確定である
 * null であればログインしていない（ログアウトされた）
 */
export const currentAuthAtom = atomWithDefault<
  firebase.UserInfo | null | undefined
>(async () => {
  // OAuth プロバイダからリダイレクトされた状態なら認証を行う
  // ブラウザ ウィンドウを閉じたり React Native でアクティビティが破棄されたりした場合でも、状態が維持されることを示します。この状態をクリアするには、明示的なログアウトが必要です
  await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
  return firebase.auth().currentUser as firebase.UserInfo | null;
});

/**
 * チーム識別子の配列 (Jotai 版)
 */
export const teamIdsJotaiAtom = atom<string[]>([]);

/**
 * チームの基本情報 (Jotai 版)
 */
export const teamsJotaiAtom = atomFamily((id: string) =>
  atom(async () => {
    const snapshot = await firebase
      .firestore()
      .collection('teams')
      .doc(id)
      .withConverter(convertToTeam)
      .get();
    return snapshot.data();
  })
);

/**
 * チームに紐づいている Stripe の顧客情報 (Jotai 版)
 */
export const teamCustomersJotaiAtom = atomFamily((teamId: string) => {
  const baseAtom = atom<Stripe.Customer | undefined>(undefined);

  const readAtom = atom(async get => {
    const cached = get(baseAtom);
    if (cached !== undefined) return cached;
    return requestWithAuth<Stripe.Customer>(
      endpoint + `/teamCustomers?teamId=${teamId}`
    );
  });

  return atom(
    get => get(readAtom),
    (
      get,
      set,
      newValue:
        | Stripe.Customer
        | undefined
        | ((prev: Stripe.Customer | undefined) => Stripe.Customer | undefined)
        | typeof RESET
    ) => {
      if (newValue === RESET) {
        set(baseAtom, undefined); // キャッシュをクリアして再取得を促す
        return;
      }
      if (typeof newValue === 'function') {
        const prev = get(baseAtom);
        set(baseAtom, newValue(prev));
      } else {
        set(baseAtom, newValue);
      }
    }
  );
});

/**
 * あるチームに所属しているチームメンバーの ID を全て列挙する (Jotai 版)
 */
export const teamMemberIdsJotaiAtom = atomFamily((teamId: string) => {
  const baseAtom = atom<string[]>([]);
  const derivedAtom = atom(
    get => get(baseAtom),
    (get, set, newValue: string[] | ((prev: string[]) => string[])) => {
      if (typeof newValue === 'function') {
        const prev = get(baseAtom);
        set(baseAtom, newValue(prev));
      } else {
        set(baseAtom, newValue);
      }
    }
  );
  return derivedAtom;
});

/**
 * チームメンバーの一人一人の詳細情報を保持する (Jotai 版)
 *
 * 注意: atomFamilyはオブジェクトパラメータを参照で比較するため、
 * 文字列キー(teamId::uid)を使用しています
 */
type MemberValue = IMember | undefined;
type MemberUpdate = MemberValue | ((prev: MemberValue) => MemberValue);
type MemberAtom = WritableAtom<MemberValue, MemberUpdate, void>;

const memberByKeyFamily = atomFamily<string, MemberAtom>(key => {
  const baseAtom = atom<MemberValue>(undefined);
  return atom<MemberValue, MemberUpdate, void>(
    get => get(baseAtom),
    (get, set, update: MemberUpdate) => {
      const next =
        typeof update === 'function'
          ? (update as (p: MemberValue) => MemberValue)(get(baseAtom))
          : update;
      set(baseAtom, next);
    }
  );
});

/**
 * 外部APIを維持するためのラッパー関数
 * オブジェクトパラメータを受け取り、内部的には文字列キーでatomを取得
 */
const memberKey = (teamId: string, uid: string) => `${teamId}::${uid}`;

export const teamMembersJotaiAtom = (params: {
  teamId: string;
  uid: string;
}): MemberAtom => memberByKeyFamily(memberKey(params.teamId, params.uid));

/**
 * チームメンバーの一括書き込み用 atom (Jotai 版)
 * Firestore の onSnapshot 内で複数のメンバーデータを書き込むために使用
 */
export const teamMembersWriterJotaiAtom = atomFamily((teamId: string) =>
  atom(
    null,
    (
      get,
      set,
      updates:
        | { uid: string; member: IMember | undefined }
        | Array<{ uid: string; member: IMember | undefined }>
    ) => {
      const arr = Array.isArray(updates) ? updates : [updates];
      for (const { uid, member } of arr) {
        set(teamMembersJotaiAtom({ teamId, uid }), member);
      }
    }
  )
);

/**
 * メンバーの識別に使われているタグの一覧 (Jotai 版)
 */
export const completeTagsJotaiAtom = atomFamily((teamId: string) => {
  const baseAtom = atom<string[]>([]);
  const derivedAtom = atom(
    get => get(baseAtom),
    (get, set, newValue: string[] | ((prev: string[]) => string[])) => {
      if (typeof newValue === 'function') {
        const prev = get(baseAtom);
        set(baseAtom, newValue(prev));
      } else {
        set(baseAtom, newValue);
      }
    }
  );
  return derivedAtom;
});

/**
 * 管理者の詳細情報 (Jotai 版)
 */
type AdminDetailsValue = { [uid: string]: IAdminDetail | undefined };
type AdminDetailsUpdate =
  | AdminDetailsValue
  | ((prev: AdminDetailsValue) => AdminDetailsValue)
  | typeof RESET;

export const adminDetailsJotaiAtom = atomFamily((teamId: string) => {
  const baseAtom = atom<AdminDetailsValue | undefined>(undefined);

  const readAtom = atom(async get => {
    const cached = get(baseAtom);
    if (cached !== undefined) return cached;

    const adminDetails: { [uid: string]: IAdminDetail | undefined } = {};
    const qs = await firebase
      .firestore()
      .collection('teams')
      .doc(teamId)
      .collection('adminDetails')
      .withConverter(convertToAdminDetail)
      .get();
    qs.forEach(ds => {
      adminDetails[ds.id] = ds.data();
    });
    return adminDetails;
  });

  return atom(
    get => get(readAtom),
    (get, set, newValue: AdminDetailsUpdate) => {
      if (newValue === RESET) {
        set(baseAtom, undefined);
        return;
      }
      if (typeof newValue === 'function') {
        const prev = get(baseAtom) ?? {};
        set(baseAtom, newValue(prev));
      } else {
        set(baseAtom, newValue);
      }
    }
  );
});

/**
 * チームメンバーのローディング状態 (Jotai 版)
 */
export const loadingTeamMembersJotaiAtom = atomFamily((teamId: string) => {
  const baseAtom = atom<boolean>(true);
  const derivedAtom = atom(
    get => get(baseAtom),
    (get, set, newValue: boolean) => {
      set(baseAtom, newValue);
    }
  );
  return derivedAtom;
});

/**
 * 支払いが有効になっているチームメンバーの人数を取得する (Jotai 版)
 */
export const activeMembersNumJotaiAtom = atomFamily((teamId: string) =>
  atom(async get => {
    const ids = await get(teamMemberIdsJotaiAtom(teamId));
    const members = await Promise.all(
      ids.map(uid => get(teamMembersJotaiAtom({ teamId, uid })))
    );
    return members.filter(member => member?.rights).length;
  })
);

/**
 * メンバーの作品一覧を取得する (Jotai 版)
 */
export const memberWorksJotaiAtom = atomFamily(
  (params: { teamId: string; uid: string }) =>
    atom(async () => {
      const { uid, teamId } = params;
      const url = `${endpoint}/memberWorks?teamId=${teamId}&memberId=${uid}`;
      const result = await requestWithAuth(url);
      return result.works as IWork[];
    }),
  (a, b) => a.teamId === b.teamId && a.uid === b.uid
);

/**
 * 管理者がユーザーのパスワードを変更した履歴 (Jotai 版)
 */
type PasswordHistoriesValue = IPasswordHistory[];
type PasswordHistoriesUpdate = PasswordHistoriesValue | typeof RESET;

const passwordHistoriesFamily = atomFamily((key: string) => {
  const [teamId, uid] = key.split('::');
  const baseAtom = atom<PasswordHistoriesValue | undefined>(undefined);

  const readAtom = atom(async get => {
    const cached = get(baseAtom);
    if (cached !== undefined) return cached;

    const qs = await firebase
      .firestore()
      .collection('teams')
      .doc(teamId)
      .collection('passwordHistories')
      .where('uid', '==', uid)
      .orderBy('updatedAt', 'desc')
      .withConverter(convertToPasswordHistory)
      .get();
    return qs.docs.map(ds => ds.data());
  });

  return atom(
    get => get(readAtom),
    (get, set, newValue: PasswordHistoriesUpdate) => {
      if (newValue === RESET) {
        set(baseAtom, undefined);
      } else {
        set(baseAtom, newValue);
      }
    }
  );
});

const passwordHistoriesKey = (teamId: string, uid: string) =>
  `${teamId}::${uid}`;

export const passwordHistoriesJotaiAtom = (params: {
  teamId: string;
  uid: string;
}) => passwordHistoriesFamily(passwordHistoriesKey(params.teamId, params.uid));
